import React, {useState, useEffect} from 'react';
import './App.css';
import { RedocStandalone } from "redoc";
import { load } from 'js-yaml'; 
import {sanitize} from "dompurify";

const ALLOWED_CONSOLES = [
  "https://console.dev.api.trimblecloud.com",
  "https://console-dev2.dev.api.trimblecloud.com",
  "https://console.qa.api.trimblecloud.com",
  "https://console-fedramp.qa.api.trimblecloud.com",
  "https://console.stage.api.trimblecloud.com",
  "https://console.trimble.com",
];

function validateIframeAccess(): { allowed: boolean; reason: string } {
  if (window.self === window.top) {
    return { allowed: false, reason: "Direct access is not permitted" };
  }

  const referrer = document.referrer;

  if (referrer) {
    try {
      const origin = new URL(referrer).origin;
      if (ALLOWED_CONSOLES.includes(origin)) {
        return { allowed: true, reason: "" };
      }
      return { allowed: false, reason: `Unauthorized parent domain: ${origin}` };
    } catch {
      return { allowed: false, reason: "Invalid referrer URL" };
    }
  }
  // Referrer hidden by parent's Referrer-Policy
  if (window.location.ancestorOrigins && window.location.ancestorOrigins.length > 0) {
    const parentOrigin = window.location.ancestorOrigins[0];
    if (ALLOWED_CONSOLES.includes(parentOrigin)) {
      return { allowed: true, reason: "" };
    }
    return { allowed: false, reason: `Unauthorized parent domain: ${parentOrigin}` };
  }
  return { allowed: true, reason: "" };
}

export function sanitizeJsonValues(input: any): unknown {
  if (typeof input === 'string') {
    // Sanitize string values
    return sanitize(input);
  } else if (Array.isArray(input)) {
    // If the input is an array, recursively sanitize each element
    return input.map(item => sanitizeJsonValues(item));
  } else if (typeof input === 'object' && input !== null) {
    // If the input is an object, recursively sanitize each key-value pair
    const sanitizedObject = {} as Record<string, unknown>;
    for (const [key, value] of Object.entries(input)) {
      sanitizedObject[key] = sanitizeJsonValues(value) as any;
    }
    return sanitizedObject;
  } else {
    // For other types (numbers, booleans, null), return as is
    return input;
  }
}

export async function fetchAndSanitizeSpec(url: string) {
  try {
    const response = await fetch(url);
    let rawData = await response.text(); // Fetch raw text data

    let data;
    if (url.endsWith('.json')) {
      try {
        data = JSON.parse(rawData); // Parse as JSON
      } catch (jsonError) {
        console.error('Error parsing JSON:', jsonError);
        console.error('Raw data:', rawData);
        throw new Error('Invalid JSON format');
      }
    } else if (url.endsWith('yaml') || url.endsWith('yml')) {
      try {
        data = load(rawData); // Parse as YAML
      } catch (yamlError) {
        console.error('Error parsing YAML:', yamlError);
        console.error('Raw data:', rawData);
        throw new Error('Invalid YAML format');
      }
    } else {
      console.error('Unsupported content type');
      console.error('Raw data:', rawData);
      throw new Error('Unsupported content type');
    }

    // Sanitize if the data is a string
    let sanitizedData;
    if (typeof data === 'string') {
      sanitizedData = sanitize(data);
    } else {
      sanitizedData = sanitizeJsonValues(data)
    }

    return sanitizedData;
  } catch (error) {
    console.error('Error fetching or sanitizing data:', error);
    return null;
  }
}




export const App = (props: any) => {
  const queryParams = new URLSearchParams(window.location.search);
    const specUrl = queryParams.get('specUrl');

  const [spec, setSpec] = useState<any>();
  const [error, setError] = useState<any>(null);
  const [isForbidden, setIsForbidden] = useState(false);

  useEffect(() => {
    const { allowed } = validateIframeAccess();
    if (!allowed) {
      setIsForbidden(true);
    }
  }, []);

  useEffect(() => {
    async function loadSpec() {
      if(!specUrl) {
        return;
      }
      try {
        const sanitizedSpec = await fetchAndSanitizeSpec(specUrl);
        setSpec(sanitizedSpec as any);
      } catch (err) {
        setError(err);
      }
    }

    loadSpec();
  }, [specUrl]);

  if (isForbidden) {
    return <div className="error">403 Forbidden - Unauthorized Access</div>;
  }

  if (error) {
    return <div>Error loading spec: {error.message}</div>;
  }
  if (!specUrl) {
    return <div className="error">API specification not available</div>;
  }

  if (!spec) {
    return <div>Loading...</div>;
  }
  return (
    <main>
      {specUrl && spec &&
      <RedocStandalone
          spec={spec}
          options={{
            nativeScrollbars: true,
            hideDownloadButton : true,
            theme: { 
              colors: 
              {
                primary: { 
                  main: '#005f9e' 
                } 
              },  
              typography: {
                fontFamily: "'Open Sans', sans-serif"
              }
            },
            
          }}
        />
      }
    </main>
  );
}

export default App;
